/**
 * One reported Issue. The reporter sees what they sent, its status and owner, the
 * conversation, and can comment or close it. The ADMIN role also gets the controls
 * (status, priority, owner), the captured console and network trails, the un-marked
 * screenshot when one was kept, the Delyver push status, and Copy for Claude Code.
 */

import { Link } from "@tanstack/react-router";
import { ArrowLeft, ClipboardCopy, Download, Expand, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useCloseSupportIssue,
  useCommentOnSupportIssue,
  useRetryDelyverPush,
  useSupportIssue,
  useSupportIssueAssignees,
  useUpdateSupportIssue,
} from "@/hooks/use-support-issues";
import { buildIssueClaudeMarkdown } from "@/lib/issue-claude-markdown";
import {
  CATEGORY_LABELS,
  isSupportIssuePriority,
  isSupportIssueStatus,
  PRIORITY_LABELS,
  STATUS_LABELS,
  SUPPORT_ISSUE_PRIORITIES,
  SUPPORT_ISSUE_STATUSES,
} from "@/lib/support-issue-labels";
import type { SupportIssueDetail } from "@/schemas/support-issues";
import { IssueStatusBadge } from "./IssueStatusBadge";

const UNASSIGNED = "unassigned";

/** Keys for a list of strings that may repeat: the text plus which occurrence it is. */
function withStableKeys(values: string[]): Array<{ key: string; value: string }> {
  const seen = new Map<string, number>();
  return values.map((value) => {
    const n = (seen.get(value) ?? 0) + 1;
    seen.set(value, n);
    return { key: `${value}#${n}`, value };
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm">{children}</dd>
    </div>
  );
}

function ScreenshotCard({ issue }: { issue: SupportIssueDetail }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { screenshot } = issue;
  if (screenshot.state === "none") return null;

  const variant = showOriginal && screenshot.hasOriginal ? "original" : "marked";
  const src = `/api/support-issues/${issue.id}/screenshot${variant === "original" ? "?variant=original" : ""}`;
  const label = screenshot.markedUp
    ? variant === "original"
      ? "Screenshot without the reporter's markup"
      : "Screenshot, marked up by the reporter"
    : "Screenshot";

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">{label}</CardTitle>
        {screenshot.state === "stored" && (
          <div className="flex flex-wrap items-center gap-1">
            {screenshot.hasOriginal && (
              <Button size="sm" variant="ghost" onClick={() => setShowOriginal((v) => !v)}>
                {showOriginal ? "View with markup" : "View without markup"}
              </Button>
            )}
            <Button
              size="icon-sm"
              variant="ghost"
              aria-label="Expand"
              onClick={() => setExpanded(true)}
            >
              <Expand className="size-4" />
            </Button>
            <Button size="icon-sm" variant="ghost" aria-label="Download" asChild>
              <a href={src} download>
                <Download className="size-4" />
              </a>
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {screenshot.state === "stored" ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="overflow-hidden rounded-md border border-border bg-muted"
            aria-label="Expand the screenshot"
          >
            <img src={src} alt={label} className="max-h-[480px] w-full object-contain" />
          </button>
        ) : (
          <p className="text-sm text-muted-foreground">
            {issue.delyver?.status === "sent"
              ? "File storage is off in this environment, so Staff didn't keep this screenshot. Delyver has it with the issue."
              : "File storage is off in this environment, so Staff didn't keep this screenshot."}
          </p>
        )}
        {screenshot.redacted && (
          <p className="text-xs text-muted-foreground">
            The reporter blacked out part of the image, so no un-marked copy was kept.
          </p>
        )}
        {screenshot.notes.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground">Notes on the screenshot</h3>
            <ul className="mt-1 list-disc pl-5 text-sm">
              {withStableKeys(screenshot.notes).map(({ key, value }) => (
                <li key={key}>{value}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="flex h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col p-3">
          <DialogTitle className="sr-only">{label}</DialogTitle>
          <div className="min-h-0 flex-1 overflow-auto">
            <img src={src} alt={label} className="mx-auto h-auto max-w-full" />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Timeline({ issue }: { issue: SupportIssueDetail }) {
  const comment = useCommentOnSupportIssue(issue.id);
  const [body, setBody] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Activity</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ol className="flex flex-col gap-3">
          {issue.timeline.map((entry) => (
            <li key={entry.id} className="flex flex-col gap-0.5">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{entry.actorName ?? "Someone"}</span>
                {" · "}
                {formatDateTime(entry.at)}
              </p>
              {entry.kind === "comment" ? (
                <p className="whitespace-pre-wrap rounded-md bg-muted px-3 py-2 text-sm">
                  {entry.text}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">{entry.text}</p>
              )}
            </li>
          ))}
        </ol>
        {issue.canComment && (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!body.trim()) return;
              comment.mutate(body, { onSuccess: () => setBody("") });
            }}
          >
            <Textarea
              aria-label="Add a comment"
              placeholder={issue.canWork ? "Reply to the reporter…" : "Add more detail or reply…"}
              rows={3}
              maxLength={5000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!body.trim() || comment.isPending}>
                {comment.isPending ? "Sending…" : "Comment"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function WorkControls({ issue }: { issue: SupportIssueDetail }) {
  const update = useUpdateSupportIssue(issue.id);
  const assignees = useSupportIssueAssignees(issue.canWork);

  return (
    <>
      <DetailRow label="Status">
        <Select
          value={issue.status}
          disabled={update.isPending}
          onValueChange={(v) => {
            if (isSupportIssueStatus(v)) update.mutate({ status: v });
          }}
        >
          <SelectTrigger className="w-full" aria-label="Status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUPPORT_ISSUE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DetailRow>
      <DetailRow label="Priority">
        <Select
          value={issue.priority}
          disabled={update.isPending}
          onValueChange={(v) => {
            if (isSupportIssuePriority(v)) update.mutate({ priority: v });
          }}
        >
          <SelectTrigger className="w-full" aria-label="Priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUPPORT_ISSUE_PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DetailRow>
      <DetailRow label="Owner">
        <Select
          value={issue.ownerUserId ?? UNASSIGNED}
          disabled={update.isPending || assignees.isLoading}
          onValueChange={(v) => update.mutate({ ownerUserId: v === UNASSIGNED ? null : v })}
        >
          <SelectTrigger className="w-full" aria-label="Owner">
            <SelectValue placeholder={issue.ownerName ?? "Unassigned"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
            {(assignees.data ?? []).map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
            {/* Keep a current owner visible even if they are no longer an assignable Admin. */}
            {issue.ownerUserId &&
              !(assignees.data ?? []).some((a) => a.id === issue.ownerUserId) && (
                <SelectItem value={issue.ownerUserId}>
                  {issue.ownerName ?? "Former owner"}
                </SelectItem>
              )}
          </SelectContent>
        </Select>
        {assignees.isError && (
          <p className="mt-1 text-xs text-destructive">Admins couldn't be loaded.</p>
        )}
      </DetailRow>
    </>
  );
}

function DelyverCard({ issue }: { issue: SupportIssueDetail }) {
  const retry = useRetryDelyverPush(issue.id);
  const sync = issue.delyver;
  if (!sync) return null;
  const text =
    sync.status === "sent"
      ? `Sent${sync.shortId ? ` as ${sync.shortId}` : ""}${sync.syncedAt ? ` on ${formatDateTime(sync.syncedAt)}` : ""}.`
      : sync.status === "skipped"
        ? "Not sent — Delyver isn't set up in this environment."
        : sync.status === "pending"
          ? "Sending…"
          : `Not sent yet after ${sync.attempts} attempt${sync.attempts === 1 ? "" : "s"}.`;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Delyver</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        <p>{text}</p>
        {sync.lastError && sync.status === "failed" && (
          <p className="break-words text-xs text-muted-foreground">{sync.lastError}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Changes made in Delyver don't come back to Staff.
        </p>
        {(sync.status === "failed" || sync.status === "pending") && (
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            disabled={retry.isPending}
            onClick={() => retry.mutate()}
          >
            <RefreshCw className="size-4" />
            {retry.isPending ? "Sending…" : "Send to Delyver now"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function DiagnosticsCard({ issue }: { issue: SupportIssueDetail }) {
  const d = issue.diagnostics;
  if (!d) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Captured diagnostics</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <details>
          <summary className="cursor-pointer font-medium">
            Console ({d.consoleTrail.length})
          </summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-muted p-2 font-mono text-xs whitespace-pre-wrap">
            {d.consoleTrail.length === 0
              ? "No errors or warnings were captured."
              : d.consoleTrail
                  .map((e) => `[${e.at}] ${e.level.toUpperCase()} ${e.message}`)
                  .join("\n")}
          </pre>
        </details>
        <details>
          <summary className="cursor-pointer font-medium">
            Network ({d.networkTrail.length})
          </summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded-md bg-muted p-2 font-mono text-xs whitespace-pre-wrap">
            {d.networkTrail.length === 0
              ? "No requests were captured."
              : d.networkTrail
                  .map((e) =>
                    e.kind === "navigation"
                      ? `[${e.at}] NAV ${e.path}`
                      : `[${e.at}] ${e.method ?? "GET"} ${e.path} → ${e.status ?? "failed"}${e.durationMs !== undefined ? ` (${e.durationMs}ms)` : ""}`,
                  )
                  .join("\n")}
          </pre>
        </details>
      </CardContent>
    </Card>
  );
}

export function IssueDetail({ id }: { id: string }) {
  const { data: issue, isLoading, isError, error, refetch } = useSupportIssue(id);
  const close = useCloseSupportIssue(id);
  const [confirmClose, setConfirmClose] = useState(false);

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }
  if (isError || !issue) {
    const notFound = error instanceof Error && /not found/i.test(error.message);
    return (
      <PageContainer>
        <ErrorState
          message={
            notFound
              ? "This issue doesn't exist, or it isn't one you can see."
              : error instanceof Error
                ? error.message
                : "The issue couldn't be loaded."
          }
          onRetry={notFound ? undefined : () => refetch()}
        />
      </PageContainer>
    );
  }

  const copyForClaude = async () => {
    try {
      await navigator.clipboard.writeText(buildIssueClaudeMarkdown(issue, window.location.href));
      toast.success("Copied — paste it into Claude Code");
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  return (
    <PageContainer>
      <Link
        to="/dashboard/issues"
        className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Issues
      </Link>
      <PageHeader
        eyebrow={issue.code}
        title={issue.title}
        description={`Reported by ${issue.reporterName} on ${formatDateTime(issue.createdAt)}${
          issue.route ? ` · on ${issue.route}` : ""
        }`}
        actions={
          <>
            {issue.canWork && (
              <Button variant="outline" onClick={copyForClaude}>
                <ClipboardCopy className="size-4" />
                Copy for Claude Code
              </Button>
            )}
            {issue.canClose && (
              <Button
                variant={issue.canWork ? "ghost" : "outline"}
                onClick={() => setConfirmClose(true)}
                disabled={close.isPending}
              >
                Close issue
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-[var(--page-gap)] lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-[var(--page-gap)]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What happened</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{issue.description}</p>
            </CardContent>
          </Card>
          <ScreenshotCard issue={issue} />
          <Timeline issue={issue} />
        </div>

        <div className="flex min-w-0 flex-col gap-[var(--page-gap)]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="flex flex-col gap-3">
                {issue.canWork ? (
                  <WorkControls issue={issue} />
                ) : (
                  <>
                    <DetailRow label="Status">
                      <IssueStatusBadge status={issue.status} />
                    </DetailRow>
                    <DetailRow label="Priority">{PRIORITY_LABELS[issue.priority]}</DetailRow>
                    <DetailRow label="Owner">
                      {issue.ownerName ? `Assigned to ${issue.ownerName}` : "Not assigned yet"}
                    </DetailRow>
                  </>
                )}
                {issue.category && (
                  <DetailRow label="Category">{CATEGORY_LABELS[issue.category]}</DetailRow>
                )}
                {issue.canWork && (
                  <DetailRow label="Reported by">
                    <p>{issue.reporterName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {issue.reporterRole} · {issue.reporterEmail}
                    </p>
                  </DetailRow>
                )}
                {issue.diagnostics?.pageUrl && (
                  <DetailRow label="Page">
                    <p className="break-all text-xs">{issue.diagnostics.pageUrl}</p>
                  </DetailRow>
                )}
                {issue.diagnostics && (
                  <DetailRow label="Browser">
                    <p className="break-words text-xs text-muted-foreground">
                      {[issue.diagnostics.userAgent, issue.diagnostics.viewport]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </p>
                  </DetailRow>
                )}
                {issue.diagnostics?.appVersion && (
                  <DetailRow label="App version">v{issue.diagnostics.appVersion}</DetailRow>
                )}
                <DetailRow label="Last updated">{formatDateTime(issue.updatedAt)}</DetailRow>
                {issue.resolvedAt && (
                  <DetailRow label="Finished">{formatDateTime(issue.resolvedAt)}</DetailRow>
                )}
              </dl>
            </CardContent>
          </Card>
          <DelyverCard issue={issue} />
          <DiagnosticsCard issue={issue} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmClose}
        onOpenChange={setConfirmClose}
        title={`Close ${issue.code}?`}
        description={
          issue.canWork
            ? "The issue moves to Closed and the reporter is told."
            : "Close it if it's sorted or no longer needed. The team can still see it."
        }
        confirmLabel="Close issue"
        loading={close.isPending}
        onConfirm={() => close.mutate(undefined, { onSuccess: () => setConfirmClose(false) })}
      />
    </PageContainer>
  );
}
